import { Location } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgcCookieConsentService } from 'ngx-cookieconsent';
import { filter, take, takeUntil } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { NavigatorService } from 'src/app/core/navigator/navigator.service';
import { Unsubscriber } from 'src/app/core/unsubscriber/unsubscriber';

@Component({
  selector: 'sis-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent extends Unsubscriber implements OnInit {
  backgroundColor = 'white';

  readonly routesWithoutCookieBanner = ['/sismap/', '/windmonitor/', '/report/'];

  constructor(
    @Inject(TranslateService) private translateService: TranslateService,
    private appService: AppService,
    @Inject(NgcCookieConsentService) private ccService: NgcCookieConsentService,
    private navigatorService: NavigatorService,
    private location: Location,
    private activatedRoute: ActivatedRoute,
  ) {
    super();

    this.ccService.popupClose$.pipe(take(1)).subscribe(() => {
      localStorage.setItem('cookieAccepted', 'true');
    });

    this.translateService.onLangChange
      .pipe(takeUntil(this.onDestroy$), takeUntil(this.ccService.popupClose$))
      .subscribe(() => {
        this.setupCookieBanner();
      });
  }

  ngOnInit(): void {
    const browserLanguage = this.translateService.getBrowserLang();
    this.translateService.setFallbackLang(browserLanguage?.match(/de|en|fr|it/) ? browserLanguage : 'en');
    this.setupCookieBanner();
    this.activatedRoute.queryParams
      .pipe(
        takeUntil(this.onDestroy$),
        filter((queryParams) => 'language' in queryParams),
      )
      .subscribe((queryParams) => {
        this.setLanguage(queryParams['language']);
      });
    this.appService.backgroundColor$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((backgroundColor) => (this.backgroundColor = backgroundColor));
  }

  private setLanguage(language?: 'de' | 'en' | 'fr' | 'it'): void {
    // the lang to use, if the lang isn't available, it will use the current loader to get them
    const selectedLanguage = language ?? this.translateService.getBrowserLang();
    // Calling this method multiple times in quick succession can cause a race condition where one call overtakes the other
    this.translateService.use(selectedLanguage?.match(/de|en|fr|it/) ? selectedLanguage : 'en');
  }

  private setupCookieBanner(): void {
    const path = this.location.path();

    try {
      this.ccService?.destroy();
    } catch {
      return;
    }

    if (this.routesWithoutCookieBanner.some((r) => path.includes(r))) {
      return;
    }

    if (localStorage.getItem('cookieAccepted') !== 'true') {
      const cookieTextTranslationString = 'general.phrase.cookies';
      const cookieUnderstoodTranslationString = 'general.term.understood';

      const cookieConfig = this.ccService.getConfig();

      this.translateService
        .get([cookieTextTranslationString, cookieUnderstoodTranslationString])
        .pipe(take(1))
        .subscribe((translations) => {
          cookieConfig.content = {
            message: translations[cookieTextTranslationString],
            dismiss: translations[cookieUnderstoodTranslationString],
          };

          cookieConfig.cookie = {
            domain: this.navigatorService.baseUrl,
          };

          this.ccService.init(cookieConfig);
        });
    }
  }
}

